import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  InputAdornment,
  Pagination,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  RadioButtonUnchecked as PendingIcon,
  Search as SearchIcon,
  SkipNext as SkipNextIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { isInWebView } from '../../utils/saveBlobDownload';
import { formatRelative } from '../../../super-picker/components/share-links/shareLinkFormat';
import { revealOnFocus, useKeyboardSafeViewport } from './keyboardSafeViewport';

export type SheetTag = { id: string; name: string; color: string | null };

export interface SheetClient {
  id: string;
  name: string;
  /** Display form, e.g. "+65 9123 4567". */
  mobile: string;
  tags: SheetTag[];
  /** When this item was last sent to the client; null = never or not tracked. */
  lastSentAt: string | null;
}

export type SheetTemplate = { key: string; label: string; body: string };

/** A ready-to-tap chat for one client: the wa.me href and the exact text it carries. */
export type PreparedSend = { href: string; text: string };

/** What a caller plugs into the sheet: where clients come from and how each send is built. */
export interface SendToClientsAdapter<C extends SheetClient = SheetClient> {
  /** Second header line, e.g. "Thomson Reserve · 3D Map". */
  subtitle: string;
  load(): Promise<{ clients: C[]; agentName: string }>;
  templates: SheetTemplate[];
  /** Insert chips in the message step; one of them is the link. */
  placeholders: Array<{ token: string; label: string }>;
  /** Matches the link placeholder; a message without it gets a warning and can't be sent. */
  linkToken: RegExp;
  /** Preview text for one client (or none yet), filled as far as the caller can. */
  preview(message: string, client: C | null, agentName: string): string;
  /** Build the chat for one client. Runs before the tap, since a chat opened after an await is blocked. */
  prepare(client: C, message: string, agentName: string): Promise<PreparedSend>;
  /** Record a send that was just opened in WhatsApp. */
  recordSend(client: C, text: string): Promise<{ lastSentAt?: string | null } | void>;
  /** The message step is previewing this client; start any slow work its preview needs. */
  warm?(client: C): void;
  /** Called once each time the sheet opens. */
  onOpen?(): void;
  /** Called on each Send tap. */
  onSent?(client: C): void;
  /** Explains who is listed, under "No clients to send to yet". */
  emptyHint: string;
  /** Under "Sent to N clients" on the final step. */
  doneHint: string;
}

type Step = 'pick' | 'message' | 'send';
type PrepareState = { status: 'loading' } | { status: 'ready'; send: PreparedSend } | { status: 'error'; error: string };

/** Rows drawn at once; past this the agent narrows with search or a tag. */
const PAGE_SIZE = 20;
const WA_GREEN = '#25d366';
// --color-surface is translucent in some themes (classic: 85% white); layer it over the page colour so the sheet stays opaque.
const SOLID_SURFACE = { bgcolor: 'var(--color-bg)', backgroundImage: 'linear-gradient(var(--color-surface), var(--color-surface))' } as const;

const isEoiTag = (tag: { name: string }) => tag.name.trim().toUpperCase() === 'EOI';
const tagColor = (tag: { color: string | null; name: string }) => tag.color || (isEoiTag(tag) ? '#ed8936' : '#64748b');

function initials(name: string): string {
  const words = name.replace(/[^A-Za-z\s]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '#';
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
}

/**
 * "Send to my clients": pick Super Connector clients (EOI pre-filtered), write
 * one message, then open a pre-filled WhatsApp chat per client, one tap each.
 * wa.me can only open one chat per tap, so the last step walks the selection
 * as a queue. Each client's chat is prepared before its turn so the tap can
 * open WhatsApp directly.
 *
 * Mobile: full screen, sized to the area above the on-screen keyboard so the
 * footer action never hides behind it (see `useKeyboardSafeViewport`).
 */
export function SendToClientsSheet<C extends SheetClient>({ open, resetKey, adapter, onClose }: {
  open: boolean;
  /** A new value starts a fresh sheet (e.g. a different link or file). */
  resetKey: string;
  adapter: SendToClientsAdapter<C>;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const viewport = useKeyboardSafeViewport(open && isMobile);
  const keyboardOpen = !!viewport && window.innerHeight - viewport.height > 120;
  const [fieldFocused, setFieldFocused] = useState(false);
  // The Android WebView's zoom pill floats over the bottom-right corner; lift the footer clear of it.
  const clearZoomControls = isMobile && !keyboardOpen && isInWebView();

  const [step, setStep] = useState<Step>('pick');
  const [clients, setClients] = useState<C[] | null>(null);
  const [agentName, setAgentName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [templateKey, setTemplateKey] = useState('');
  const [message, setMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(true);
  const [queue, setQueue] = useState<string[]>([]);
  const [cursor, setCursor] = useState(0);
  const [status, setStatus] = useState<Record<string, 'sent' | 'skipped'>>({});
  const [prepared, setPrepared] = useState<Record<string, PrepareState>>({});
  // Bumped whenever prepared chats are discarded, so late results from before are ignored.
  const prepareRound = useRef(0);
  const [toast, setToast] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // The adapter is rebuilt by its owner on every render; read the latest through a ref
  // so effects keyed on the sheet's own state don't re-run because of it.
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  // Fresh sheet every time it opens for an item.
  useEffect(() => {
    if (!open) return;
    const current = adapterRef.current;
    setStep('pick');
    setQuery('');
    setSelected(new Set());
    setTagFilter(new Set());
    setTemplateKey(current.templates[0]?.key ?? '');
    setMessage(current.templates[0]?.body ?? '');
    setQueue([]);
    setCursor(0);
    setStatus({});
    prepareRound.current += 1;
    setPrepared({});
    setClients(null);
    setLoadError(null);
    current.onOpen?.();
  }, [open, resetKey]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    adapterRef.current.load()
      .then((res) => {
        if (cancelled) return;
        setClients(res.clients);
        setAgentName(res.agentName);
        // Default to the EOI list when the agent has one.
        const eoi = res.clients.flatMap((c) => c.tags).find(isEoiTag);
        setTagFilter(eoi ? new Set([eoi.id]) : new Set());
      })
      .catch((err: Error) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [open, resetKey, reloadKey]);

  // Tags on at least one client, with counts; EOI first, then by size.
  const tagOptions = useMemo(() => {
    const byId = new Map<string, SheetTag & { count: number }>();
    for (const c of clients ?? []) {
      for (const t of c.tags) {
        const entry = byId.get(t.id);
        if (entry) entry.count += 1;
        else byId.set(t.id, { ...t, count: 1 });
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      Number(isEoiTag(b)) - Number(isEoiTag(a)) || b.count - a.count || a.name.localeCompare(b.name));
  }, [clients]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    return (clients ?? []).filter((c) => {
      if (tagFilter.size > 0 && !c.tags.some((t) => tagFilter.has(t.id))) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q)
        || (qDigits.length >= 3 && c.mobile.replace(/\D/g, '').includes(qDigits))
        || c.tags.some((t) => t.name.toLowerCase().includes(q));
    });
  }, [clients, query, tagFilter]);

  const byId = useMemo(() => new Map((clients ?? []).map((c) => [c.id, c])), [clients]);
  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.id));

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [query, tagFilter, open, resetKey]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = visible.slice(pageStart, pageStart + PAGE_SIZE);

  const toggleTag = (id: string | null) => {
    setTagFilter((prev) => {
      if (id === null) return new Set();
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleClient = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((c) => next.delete(c.id));
      else visible.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const insertPlaceholder = (token: string) => {
    const el = messageRef.current;
    const start = el?.selectionStart ?? message.length;
    const end = el?.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setMessage(next);
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const messageHasLink = adapter.linkToken.test(message);

  const startSending = () => {
    // Keep the list order the agent saw.
    const order = (clients ?? []).filter((c) => selected.has(c.id)).map((c) => c.id);
    setQueue(order);
    setCursor(0);
    setStatus({});
    // Chats carry the message as written now; edits since the last visit rebuild them.
    prepareRound.current += 1;
    setPrepared({});
    setStep('send');
  };

  const nextPending = (from: number, current: Record<string, 'sent' | 'skipped'>) => {
    for (let i = from; i < queue.length; i += 1) if (!current[queue[i]]) return i;
    return queue.length;
  };

  // Prepare the client whose turn it is, and the one after, so each tap is ready.
  useEffect(() => {
    if (step !== 'send') return;
    const ids: string[] = [];
    const first = nextPending(cursor, status);
    if (first < queue.length) ids.push(queue[first]);
    const second = nextPending(first + 1, status);
    if (second < queue.length) ids.push(queue[second]);
    ids.forEach((id) => {
      if (prepared[id]) return;
      const client = byId.get(id);
      if (!client) return;
      const round = prepareRound.current;
      const settle = (state: PrepareState) => setPrepared((prev) => (
        round === prepareRound.current && prev[id] ? { ...prev, [id]: state } : prev));
      setPrepared((prev) => ({ ...prev, [id]: { status: 'loading' } }));
      adapterRef.current.prepare(client, message, agentName)
        .then((send) => settle({ status: 'ready', send }))
        .catch((err: Error) => settle({ status: 'error', error: err.message || 'Could not prepare this chat' }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, cursor, queue, status, prepared, byId, message, agentName]);

  const retryPrepare = (id: string) => {
    setPrepared((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // The tap itself follows the wa.me link (a popup opened after an await gets
  // blocked); this only records the send and moves the queue on.
  const handleSent = (client: C, send: PreparedSend) => {
    adapter.onSent?.(client);
    const nextStatus = { ...status, [client.id]: 'sent' as const };
    setStatus(nextStatus);
    setCursor(nextPending(0, nextStatus));
    adapter.recordSend(client, send.text)
      .then((res) => {
        const lastSentAt = res && res.lastSentAt;
        if (!lastSentAt) return;
        setClients((prev) => prev?.map((c) => (c.id === client.id ? { ...c, lastSentAt } : c)) ?? prev);
      })
      .catch(() => setToast(`Sent, but couldn't save it to ${client.name || 'the client'}'s history`));
  };

  const handleSkip = (client: C) => {
    const nextStatus = { ...status, [client.id]: 'skipped' as const };
    setStatus(nextStatus);
    setCursor(nextPending(0, nextStatus));
  };

  const goTo = (next: Step) => {
    setStep(next);
    contentRef.current?.scrollTo({ top: 0 });
  };

  const sentCount = Object.values(status).filter((s) => s === 'sent').length;
  const current = step === 'send' && cursor < queue.length ? byId.get(queue[cursor]) ?? null : null;
  const currentPrepared = current ? prepared[current.id] : undefined;
  const previewClient = (clients ?? []).find((c) => selected.has(c.id)) ?? null;

  useEffect(() => {
    if (step === 'message' && previewClient) adapterRef.current.warm?.(previewClient);
  }, [step, previewClient]);

  const handleClose = onClose;

  // ── Header ──────────────────────────────────────────────────────────────
  const title = step === 'pick' ? 'Send to my clients' : step === 'message' ? 'Write your message' : 'Send on WhatsApp';
  const header = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1, sm: 2 }, py: 1.25, flexShrink: 0,
      borderBottom: '1px solid rgb(var(--color-text-rgb) / 0.1)',
    }}>
      {step !== 'pick' ? (
        <IconButton aria-label="Back" sx={{ color: 'var(--color-text-strong)' }} onClick={() => goTo(step === 'send' ? 'message' : 'pick')}>
          <ArrowBackIcon />
        </IconButton>
      ) : (
        <Box sx={{
          width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0, ml: 0.5,
          bgcolor: `${WA_GREEN}1f`, color: WA_GREEN,
        }}>
          <WhatsAppIcon />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, color: 'var(--color-text-strong)', lineHeight: 1.3 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }} noWrap>
          {adapter.subtitle}
        </Typography>
      </Box>
      <IconButton aria-label="Close" sx={{ color: 'var(--color-text-muted)' }} onClick={handleClose}><CloseIcon /></IconButton>
    </Box>
  );

  // ── Step 1: pick clients ────────────────────────────────────────────────
  const pickStep = (
    <>
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1, ...SOLID_SURFACE,
        px: { xs: 2, sm: 3 }, pt: 2, pb: 1, borderBottom: '1px solid rgb(var(--color-text-rgb) / 0.06)',
      }}>
        <TextField
          fullWidth
          size="small"
          type="search"
          placeholder="Search name, number or tag"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ enterKeyHint: 'search', autoComplete: 'off', 'aria-label': 'Search clients' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            // 16px stops iOS zooming the page on focus.
            sx: { fontSize: 16, borderRadius: 2.5, color: 'var(--color-text-strong)', bgcolor: 'rgb(var(--color-text-rgb) / 0.04)' },
          }}
        />
        {tagOptions.length > 0 && (
          <Box role="group" aria-label="Filter by tag" sx={{
            display: 'flex', gap: 1, mt: 1.25, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 }, pb: 0.5,
            overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          }}>
            <Chip
              label={`All · ${clients?.length ?? 0}`}
              onClick={() => toggleTag(null)}
              variant={tagFilter.size === 0 ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0, fontWeight: 600, height: 34,
                color: 'var(--color-text-strong)', borderColor: 'rgb(var(--color-text-rgb) / 0.25)',
              ...(tagFilter.size === 0 && { bgcolor: 'var(--color-text-strong)', color: 'var(--color-surface)' }),
              }}
            />
            {tagOptions.map((tag) => {
              const active = tagFilter.has(tag.id);
              const color = tagColor(tag);
              return (
                <Chip
                  key={tag.id}
                  label={`${tag.name} · ${tag.count}`}
                  onClick={() => toggleTag(tag.id)}
                  aria-pressed={active}
                  variant={active ? 'filled' : 'outlined'}
                  sx={{
                    flexShrink: 0, fontWeight: 600, height: 34,
                    borderColor: `${color}66`, color: active ? '#fff' : color,
                    bgcolor: active ? color : 'transparent',
                    '&:hover': { bgcolor: active ? color : `${color}14` },
                  }}
                />
              );
            })}
          </Box>
        )}
        {clients && clients.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, ml: -1 }}>
            <Checkbox
              size="small"
              checked={allVisibleSelected}
              indeterminate={!allVisibleSelected && visible.some((c) => selected.has(c.id))}
              onChange={toggleAllVisible}
              disabled={visible.length === 0}
              sx={{ color: 'rgb(var(--color-text-rgb) / 0.4)', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'var(--color-accent)' } }}
              inputProps={{ 'aria-label': 'Select all shown' }}
            />
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {allVisibleSelected ? 'Unselect' : 'Select'} all {visible.length}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        {!clients && !loadError && (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        )}
        {loadError && (
          <Alert severity="error" sx={{ m: 1 }}
            action={<Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}>
            {loadError}
          </Alert>
        )}
        {clients && clients.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>No clients to send to yet</Typography>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 1 }}>
              {adapter.emptyHint}
            </Typography>
          </Box>
        )}
        {clients && clients.length > 0 && visible.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>No clients match.</Typography>
            {(tagFilter.size > 0 || query) && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => { setQuery(''); setTagFilter(new Set()); }}>
                Show all clients
              </Button>
            )}
          </Box>
        )}
        {pageRows.map((client) => {
          const checked = selected.has(client.id);
          return (
            <Box
              key={client.id}
              component="label"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 1, minHeight: 56, borderRadius: 2,
                cursor: 'pointer', bgcolor: checked ? 'rgb(var(--color-accent-rgb) / 0.08)' : 'transparent',
                '&:hover': { bgcolor: checked ? 'rgb(var(--color-accent-rgb) / 0.12)' : 'rgb(var(--color-text-rgb) / 0.04)' },
              }}
            >
              <Checkbox checked={checked} onChange={() => toggleClient(client.id)}
                sx={{ p: 0.5, color: 'rgb(var(--color-text-rgb) / 0.4)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                inputProps={{ 'aria-label': `Select ${client.name || client.mobile}` }} />
              <Box aria-hidden sx={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                fontSize: 14, fontWeight: 700, bgcolor: 'rgb(var(--color-text-rgb) / 0.08)', color: 'var(--color-text-strong)',
              }}>
                {initials(client.name)}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: 'var(--color-text-strong)' }} noWrap>
                    {client.name || client.mobile}
                  </Typography>
                  {client.lastSentAt && (
                    <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label={`Sent ${formatRelative(client.lastSentAt)}`}
                      sx={{ height: 20, fontSize: 11, flexShrink: 0, bgcolor: `${WA_GREEN}1f`, color: '#15803d',
                        '& .MuiChip-icon': { color: 'inherit' } }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                    {client.mobile}
                  </Typography>
                  {client.tags.slice(0, 3).map((tag) => (
                    <Box key={tag.id} component="span" sx={{
                      fontSize: 10.5, fontWeight: 600, px: 0.75, borderRadius: 1, whiteSpace: 'nowrap',
                      color: tagColor(tag), bgcolor: `${tagColor(tag)}1a`,
                    }}>
                      {tag.name}
                    </Box>
                  ))}
                  {client.tags.length > 3 && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>+{client.tags.length - 3}</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        {visible.length > PAGE_SIZE && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 2 }}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(_, p) => {
                setPage(p);
                contentRef.current?.scrollTo({ top: 0 });
              }}
              siblingCount={isMobile ? 0 : 1}
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': { color: 'var(--color-text-strong)', minWidth: 36, height: 36 },
                '& .MuiPaginationItem-root.Mui-selected': { bgcolor: 'var(--color-accent)', color: '#fff' },
                '& .MuiPaginationItem-root.Mui-selected:hover': { bgcolor: 'var(--color-accent)' },
              }}
            />
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              {pageStart + 1}–{pageStart + pageRows.length} of {visible.length}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  // ── Step 2: message ─────────────────────────────────────────────────────
  const bubble = (text: string) => (
    <Box sx={{ bgcolor: '#efeae2', borderRadius: 2, p: 1.5 }}>
      <Box sx={{
        ml: 'auto', maxWidth: '92%', bgcolor: '#d9fdd3', color: '#111b21', borderRadius: '10px 0 10px 10px',
        px: 1.25, py: 1, fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        boxShadow: '0 1px 0.5px rgb(0 0 0 / 0.13)',
      }}>
        {text}
      </Box>
    </Box>
  );

  const messageStep = (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box role="group" aria-label="Message template" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {adapter.templates.map((t) => (
          <Chip
            key={t.key}
            label={t.label}
            onClick={() => { setTemplateKey(t.key); setMessage(t.body); }}
            variant={templateKey === t.key ? 'filled' : 'outlined'}
            sx={{
              height: 34, fontWeight: 600, color: 'var(--color-text-strong)', borderColor: 'rgb(var(--color-text-rgb) / 0.25)',
              ...(templateKey === t.key && { bgcolor: 'var(--color-accent)', color: '#fff' }),
            }}
          />
        ))}
      </Box>

      <Box>
        <TextField
          fullWidth
          multiline
          minRows={isMobile ? 5 : 6}
          maxRows={isMobile ? 9 : 12}
          value={message}
          onChange={(e) => { setMessage(e.target.value); setTemplateKey(''); }}
          inputRef={messageRef}
          onFocus={revealOnFocus}
          inputProps={{ 'aria-label': 'Message' }}
          InputProps={{ sx: { fontSize: 16, lineHeight: 1.5, borderRadius: 2.5, color: 'var(--color-text-strong)' } }}
          sx={{ scrollMarginTop: 8 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mr: 0.5 }}>Insert</Typography>
          {adapter.placeholders.map(({ token, label }) => (
            <Chip
              key={token}
              size="small"
              label={label}
              // Keep the textarea focused (and the keyboard up) while inserting.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertPlaceholder(token)}
              variant="outlined"
              sx={{ height: 30, fontWeight: 600, color: 'var(--color-text-strong)', borderColor: 'rgb(var(--color-text-rgb) / 0.25)' }}
            />
          ))}
        </Box>
        {!messageHasLink && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Your message has no link. Tap <strong>Link</strong> to add it back.
          </Alert>
        )}
      </Box>

      <Box>
        <Button
          size="small"
          onClick={() => setPreviewOpen((v) => !v)}
          endIcon={previewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ color: 'var(--color-text-muted)', fontWeight: 600, px: 0.5 }}
        >
          Preview{previewClient ? ` for ${previewClient.name || previewClient.mobile}` : ''}
        </Button>
        <Collapse in={previewOpen}>
          <Box sx={{ mt: 1 }}>
            {bubble(adapter.preview(message, previewClient, agentName))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );

  // ── Step 3: send queue ──────────────────────────────────────────────────
  const done = step === 'send' && queue.length > 0 && cursor >= queue.length;
  const sendStep = (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      {current && (
        <Box sx={{
          borderRadius: 3, p: 2, mb: 2, border: `1.5px solid ${WA_GREEN}66`, bgcolor: `${WA_GREEN}0d`,
        }}>
          <Typography variant="overline" sx={{ color: '#15803d', fontWeight: 700, lineHeight: 1.5 }}>
            Next · {cursor + 1} of {queue.length}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-strong)' }} noWrap>
            {current.name || current.mobile}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>{current.mobile}</Typography>
          {current.lastSentAt && (
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 0.5 }}>
              Already sent {formatRelative(current.lastSentAt)}
            </Typography>
          )}
          {currentPrepared?.status === 'error' ? (
            <Alert severity="error" sx={{ mt: 1.5 }}
              action={<Button color="inherit" size="small" onClick={() => retryPrepare(current.id)}>Retry</Button>}>
              {currentPrepared.error}
            </Alert>
          ) : (
            <Box sx={{ mt: 1.5, maxHeight: 180, overflowY: 'auto', borderRadius: 2 }}>
              {bubble(currentPrepared?.status === 'ready'
                ? currentPrepared.send.text
                : adapter.preview(message, current, agentName))}
            </Box>
          )}
          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 1 }}>
            WhatsApp opens with this message ready. Tap send there, then come back for the next client.
          </Typography>
        </Box>
      )}
      {done && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 52, color: WA_GREEN }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-strong)', mt: 1 }}>
            {sentCount > 0 ? `Sent to ${sentCount} ${sentCount === 1 ? 'client' : 'clients'}` : 'Nothing sent'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5 }}>
            {adapter.doneHint}
          </Typography>
        </Box>
      )}

      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {queue.map((id, i) => {
          const client = byId.get(id);
          if (!client) return null;
          const s = status[id];
          const isCurrent = i === cursor && !done;
          return (
            <Box component="li" key={id} sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 0.5,
              borderBottom: '1px solid rgb(var(--color-text-rgb) / 0.06)',
              opacity: s === 'skipped' ? 0.55 : 1,
            }}>
              {s === 'sent'
                ? <CheckCircleIcon sx={{ color: WA_GREEN }} />
                : s === 'skipped'
                  ? <SkipNextIcon sx={{ color: 'var(--color-text-muted)' }} />
                  : <PendingIcon sx={{ color: isCurrent ? WA_GREEN : 'rgb(var(--color-text-rgb) / 0.3)' }} />}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: isCurrent ? 700 : 500, color: 'var(--color-text-strong)' }} noWrap>
                  {client.name || client.mobile}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                  {s === 'sent' ? 'Opened in WhatsApp' : s === 'skipped' ? 'Skipped' : client.mobile}
                </Typography>
              </Box>
              {s && (
                <Button size="small" onClick={() => {
                  setStatus((prev) => { const next = { ...prev }; delete next[id]; return next; });
                  setCursor(i);
                }}>
                  {s === 'sent' ? 'Resend' : 'Send'}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // ── Footer ──────────────────────────────────────────────────────────────
  const footerSx = {
    display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
    px: { xs: 2, sm: 3 }, pt: 1.5,
    pb: clearZoomControls ? 8 : 'calc(12px + env(safe-area-inset-bottom, 0px))',
    borderTop: '1px solid rgb(var(--color-text-rgb) / 0.1)', ...SOLID_SURFACE,
  } as const;
  const primarySx = {
    flex: 1, minHeight: 48, borderRadius: 3, fontWeight: 700, fontSize: 15, textTransform: 'none',
    '&.Mui-disabled': { bgcolor: 'rgb(var(--color-text-rgb) / 0.1)', color: 'rgb(var(--color-text-rgb) / 0.5)' },
  } as const;

  let footer: React.ReactNode = null;
  if (step === 'pick') {
    footer = (
      <Box sx={footerSx}>
        {selected.size > 0 && (
          <Button onClick={() => setSelected(new Set())} sx={{ minHeight: 48, color: 'var(--color-text-muted)' }}>Clear</Button>
        )}
        <Button variant="contained" disabled={selected.size === 0} onClick={() => goTo('message')}
          sx={{ ...primarySx, bgcolor: 'var(--color-accent)' }}>
          {selected.size === 0 ? 'Select clients' : `Next · ${selected.size} selected`}
        </Button>
      </Box>
    );
  } else if (step === 'message') {
    footer = (
      <Box sx={footerSx}>
        <Button variant="contained" disabled={!message.trim() || !messageHasLink} onClick={() => { startSending(); contentRef.current?.scrollTo({ top: 0 }); }}
          startIcon={<WhatsAppIcon />}
          sx={{ ...primarySx, bgcolor: WA_GREEN, '&:hover': { bgcolor: '#1ebe5b' } }}>
          Start sending to {selected.size} {selected.size === 1 ? 'client' : 'clients'}
        </Button>
      </Box>
    );
  } else if (current) {
    const ready = currentPrepared?.status === 'ready' ? currentPrepared.send : null;
    footer = (
      <Box sx={footerSx}>
        <Button onClick={() => handleSkip(current)} sx={{ minHeight: 48, color: 'var(--color-text-muted)' }}>Skip</Button>
        <Button
          variant="contained"
          component="a"
          href={ready?.href}
          target="_blank"
          rel="noopener"
          disabled={!ready}
          onClick={() => {
            if (!ready) return;
            // Advancing re-renders this same <a> with the next client's href (or none) before the
            // browser follows the link, so let the navigation go first.
            const client = current;
            window.setTimeout(() => handleSent(client, ready), 0);
          }}
          startIcon={ready ? <WhatsAppIcon /> : currentPrepared?.status === 'error' ? undefined : <CircularProgress size={16} color="inherit" />}
          sx={{ ...primarySx, bgcolor: WA_GREEN, '&:hover': { bgcolor: '#1ebe5b' }, minWidth: 0 }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ready
              ? `Send to ${(current.name || current.mobile).split(' ')[0]} · ${cursor + 1}/${queue.length}`
              : currentPrepared?.status === 'error' ? 'Link not ready' : 'Preparing link…'}
          </Box>
        </Button>
      </Box>
    );
  } else if (done) {
    footer = (
      <Box sx={footerSx}>
        <Button variant="contained" onClick={handleClose} sx={{ ...primarySx, bgcolor: 'var(--color-accent)' }}>Done</Button>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      // Opened from cards that navigate on click; keep taps inside the sheet.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      PaperProps={{ sx: {
        ...SOLID_SURFACE, display: 'flex', flexDirection: 'column',
        ...(!isMobile && { height: 'min(760px, calc(100% - 64px))' }),
        // Mobile: end the sheet at the keyboard's top edge so the footer stays reachable.
        ...(viewport && { height: `${viewport.height}px`, maxHeight: `${viewport.height}px`, mt: `${viewport.top}px`, alignSelf: 'flex-start' }),
      } }}
    >
      {header}
      <Box
        ref={contentRef}
        onFocus={(e) => { if ((e.target as HTMLElement).matches('input[type="search"], textarea')) setFieldFocused(true); }}
        onBlur={(e) => { if ((e.target as HTMLElement).matches('input[type="search"], textarea')) setFieldFocused(false); }}
        sx={{
          flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
          // Some Android WebViews draw the keyboard over the page without resizing it, so nothing
          // reports its height. While a field has focus, leave room to scroll it clear of the keyboard.
          ...(isMobile && fieldFocused && !keyboardOpen && { pb: '50vh' }),
        }}
      >
        {step === 'pick' && pickStep}
        {step === 'message' && messageStep}
        {step === 'send' && sendStep}
      </Box>
      {footer}
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
    </Dialog>
  );
}
